import { Box } from "@mui/material";
import {
  InsertDriveFileOutlined as FileIcon,
  ImageOutlined as ImageIcon,
  PictureAsPdfOutlined as PdfIcon,
  DownloadOutlined as DownloadIcon,
} from "@mui/icons-material";
import { t as coachT } from "../dashboard/coach/coachTokens";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "heic", "bmp"];

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
};

/**
 * Which icon, from the NAME rather than from a stored content type.
 *
 * Nothing on the note records a MIME type, deliberately — the file is served straight from the CDN,
 * which sends its own headers, and a column written once and never read is one to maintain for
 * nothing. The extension is what a person reads off the filename anyway, and it is the same thing
 * their own file manager keys off.
 */
const iconFor = (name: string) => {
  const ext = extensionOf(name);
  if (ext === "pdf") return PdfIcon;
  if (IMAGE_EXTENSIONS.includes(ext)) return ImageIcon;
  return FileIcon;
};

type Props = {
  fileUrl: string;
  /** What the trainer called it. The URL's own basename is generated and unreadable. */
  fileName?: string;
};

/**
 * The file on a note, as a link to open it.
 *
 * `download` carries the ORIGINAL name, so the file lands in the trainer's downloads called
 * "בדיקות דם.pdf" rather than the CDN's generated key. It is only a hint — the attribute is ignored
 * cross-origin, and the CDN is a different origin — so the link also opens in a new tab, which is
 * the behaviour that actually holds and the more useful one for a PDF or an image anyway.
 *
 * `rel="noreferrer"` because `target="_blank"` without it hands the opened page a handle on this
 * one.
 */
const NoteAttachment = ({ fileUrl, fileName }: Props) => {
  const name = fileName || "קובץ מצורף";
  const Icon = iconFor(name);

  return (
    <Box
      component="a"
      href={fileUrl}
      target="_blank"
      rel="noreferrer"
      download={name}
      data-testid="note-attachment"
      aria-label={`פתח את הקובץ ${name}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        maxWidth: "100%",
        padding: "7px 11px",
        borderRadius: coachT.rs,
        border: `1px solid ${coachT.border}`,
        background: coachT.bg,
        color: coachT.text,
        fontSize: "12.5px",
        fontWeight: 600,
        textDecoration: "none",
        transition: "all .14s",
        "&:hover": { borderColor: coachT.green, color: coachT.green, background: coachT.greenLight },
      }}
    >
      <Icon sx={{ fontSize: 17, flex: "none" }} />
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {name}
      </Box>
      <DownloadIcon sx={{ fontSize: 15, flex: "none", opacity: 0.6 }} />
    </Box>
  );
};

export default NoteAttachment;
